import React, { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { AutoAwesome, ChatBubbleOutline, Refresh } from '@mui/icons-material';

/** Generated sections: worker field wrappers (block or inline) and trust/citation sections. */
export const GENERATED_SECTION_SELECTOR = '[data-field-id], [data-trust-section="true"]';
const HIDE_DELAY_MS = 180;
const MAX_TEXT_CHARS = 1200;

/**
 * What a generated section is, read from its DOM (attributes the editor keeps on save).
 * @param {HTMLElement} element - Section element.
 * @param {HTMLElement} root - Editor root (ProseMirror DOM).
 * @returns {{fieldId: string|null, status: string|null, evidence: string[], trust: 'cited'|'needs_source'|null, heading: string, text: string, label: string, needsInput: boolean, inline: boolean}} Info.
 */
export function describeGeneratedSection(element, root) {
  const fieldId = element.getAttribute('data-field-id');
  const status = element.getAttribute('data-field-status');
  const evidence = (element.getAttribute('data-evidence') || '').split(',').map((v) => v.trim()).filter(Boolean);
  const trustEl = element.matches('[data-trust-section="true"]') ? element : element.closest('[data-trust-section="true"]');
  let trust = null;
  if (trustEl && root.contains(trustEl)) {
    trust = (trustEl.getAttribute('data-source-line-ids') || '').trim() ? 'cited' : 'needs_source';
  }

  // Heading: the element itself (a trust heading), its first heading, or the nearest one before it.
  let heading = '';
  if (/^H[1-6]$/.test(element.tagName)) heading = element.textContent || '';
  else {
    const inner = element.querySelector('h1, h2, h3, h4, h5, h6');
    if (inner) heading = inner.textContent || '';
    else {
      let top = element;
      while (top.parentElement && top.parentElement !== root) top = top.parentElement;
      for (let node = top.previousElementSibling; node; node = node.previousElementSibling) {
        if (/^H[1-6]$/.test(node.tagName)) {
          heading = node.textContent || '';
          break;
        }
      }
    }
  }
  if (!heading && trustEl) heading = trustEl.getAttribute('data-template-goal') || '';

  const needsInput = status === 'needs_input';
  let label = 'AI-written';
  if (needsInput) label = 'Needs input';
  else if (trust === 'needs_source') label = 'Needs a source';
  else if (trust === 'cited' || evidence.length) label = 'AI-written · Cited';
  const text = (element.textContent || '').replace(/\s+/g, ' ').trim().slice(0, MAX_TEXT_CHARS);
  const inline = element.tagName === 'SPAN';
  return { fieldId, status, evidence, trust, heading: heading.replace(/\s+/g, ' ').trim(), text, label, needsInput, inline };
}

/**
 * Hover/selection chrome for generated sections, in the AI field's visual language: nothing at
 * rest; on hover, when the cursor is inside, or while its actions have keyboard focus, a light
 * dashed outline, a ✦ in the margin and a label row on the top border with "Regenerate with
 * Inky" and "Ask Inky about this". Everything is an overlay positioned over the page, so it
 * never moves the text and never touches the document (the saved HTML keeps its metadata).
 *
 * @param {object} props - Props.
 * @param {import('@tiptap/core').Editor} props.editor - Editor.
 * @param {() => HTMLElement|null} props.getContainer - Positioned element wrapping the page (overlay parent).
 * @param {(info: object) => void} props.onRegenerate - "Regenerate with Inky" / "Fill this section with Inky".
 * @param {(info: object) => void} [props.onAsk] - "Ask Inky about this".
 * @returns {JSX.Element|null} Overlay.
 */
const GeneratedSectionChrome = ({ editor, getContainer, onRegenerate, onAsk }) => {
  const [hovered, setHovered] = useState(null);
  const [selected, setSelected] = useState(null);
  const [pinned, setPinned] = useState(null);
  const [box, setBox] = useState(null);
  const hideTimerRef = useRef(null);
  const blurTimerRef = useRef(null);
  const overlayHoverRef = useRef(false);
  const toolbarRef = useRef(null);

  const root = editor && !editor.isDestroyed ? editor.view.dom : null;
  const active = pinned || hovered || selected;

  const findSection = useCallback((node) => {
    if (!root || !node) return null;
    const el = node.nodeType === 3 ? node.parentElement : node;
    const found = el && typeof el.closest === 'function' ? el.closest(GENERATED_SECTION_SELECTOR) : null;
    return found && root.contains(found) ? found : null;
  }, [root]);

  // Hover over the page text.
  useEffect(() => {
    if (!root) return undefined;
    const onOver = (event) => {
      const el = findSection(event.target);
      clearTimeout(hideTimerRef.current);
      if (el) setHovered(el);
      else if (!overlayHoverRef.current) setHovered(null);
    };
    const onLeave = () => {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = setTimeout(() => {
        if (!overlayHoverRef.current) setHovered(null);
      }, HIDE_DELAY_MS);
    };
    root.addEventListener('mouseover', onOver);
    root.addEventListener('mouseleave', onLeave);
    return () => {
      clearTimeout(hideTimerRef.current);
      root.removeEventListener('mouseover', onOver);
      root.removeEventListener('mouseleave', onLeave);
    };
  }, [root, findSection]);

  // The section holding the cursor (while the editor has focus). An editor blur that moves focus
  // into this toolbar (Tab from the text) keeps the section: the clear is deferred one tick and
  // skipped when focus landed in the toolbar, so the toolbar is still mounted to receive it.
  useEffect(() => {
    if (!editor || editor.isDestroyed) return undefined;
    const inToolbar = (node) => Boolean(node && toolbarRef.current && toolbarRef.current.contains(node));
    const update = () => {
      clearTimeout(blurTimerRef.current);
      if (editor.isDestroyed || !editor.view.hasFocus()) {
        setSelected(null);
        return;
      }
      try {
        const { node } = editor.view.domAtPos(editor.state.selection.from);
        setSelected(findSection(node));
      } catch {
        setSelected(null);
      }
    };
    const onBlur = ({ event } = {}) => {
      clearTimeout(blurTimerRef.current);
      if (inToolbar(event?.relatedTarget)) return;
      blurTimerRef.current = setTimeout(() => {
        if (inToolbar(document.activeElement)) return;
        if (!editor.isDestroyed && !editor.view.hasFocus()) setSelected(null);
      }, 0);
    };
    editor.on('selectionUpdate', update);
    editor.on('focus', update);
    editor.on('blur', onBlur);
    return () => {
      clearTimeout(blurTimerRef.current);
      editor.off('selectionUpdate', update);
      editor.off('focus', update);
      editor.off('blur', onBlur);
    };
  }, [editor, findSection]);

  // Position the overlay over the active section (relative to the page container).
  const measure = useCallback(() => {
    const container = getContainer?.();
    if (!active || !container || !active.isConnected) {
      setBox(null);
      return;
    }
    const a = active.getBoundingClientRect();
    const c = container.getBoundingClientRect();
    setBox({ top: a.top - c.top, left: a.left - c.left, width: a.width, height: a.height });
  }, [active, getContainer]);
  useLayoutEffect(() => {
    measure();
  }, [measure]);
  useEffect(() => {
    if (!active || !editor) return undefined;
    editor.on('update', measure);
    window.addEventListener('resize', measure);
    let observer = null;
    const container = getContainer?.();
    if (typeof ResizeObserver !== 'undefined' && container) {
      observer = new ResizeObserver(measure);
      observer.observe(container);
    }
    return () => {
      editor.off('update', measure);
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, [active, editor, getContainer, measure]);

  if (!editor || !active || !box || !root) return null;
  if (editor.storage.quillRuntime?.reviewActive || !editor.isEditable) return null;

  const info = describeGeneratedSection(active, root);
  const name = info.heading ? `"${info.heading}"` : 'this section';
  const regenerateLabel = info.needsInput ? `Fill ${name} with Inky` : `Regenerate ${name} with Inky`;
  const warn = info.needsInput || info.trust === 'needs_source';

  const keep = () => {
    overlayHoverRef.current = true;
    clearTimeout(hideTimerRef.current);
  };
  const release = () => {
    overlayHoverRef.current = false;
    clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      if (!overlayHoverRef.current) setHovered(null);
    }, HIDE_DELAY_MS);
  };

  return (
    <Box
      data-testid="generated-section-chrome"
      sx={{ position: 'absolute', top: box.top, left: box.left, width: box.width, height: box.height, pointerEvents: 'none', zIndex: 4 }}
    >
      <Box
        aria-hidden="true"
        sx={{ position: 'absolute', inset: '-4px -6px', border: '1px dashed var(--q-ai-border, #C9C1F2)', borderRadius: 'var(--q-radius-sm, 6px)' }}
      />
      <Box aria-hidden="true" sx={{ position: 'absolute', left: -28, top: 2, display: 'flex', color: 'var(--q-ai-text, #3A28B8)' }}>
        <AutoAwesome sx={{ fontSize: 15 }} />
      </Box>
      {/* Label row: sits fully above the outline (never over the section's text) and only its
          buttons take pointer events, so text under or near it stays clickable. */}
      <Box
        ref={toolbarRef}
        role="toolbar"
        aria-label={`AI-written section${info.heading ? `: ${info.heading}` : ''}`}
        onMouseEnter={keep}
        onMouseLeave={release}
        onFocus={() => {
          keep();
          clearTimeout(blurTimerRef.current);
          setPinned(active);
        }}
        onBlur={(event) => {
          if (event.currentTarget.contains(event.relatedTarget)) return;
          setPinned(null);
          release();
          // Focus left for somewhere other than the editor: drop the kept section too (going
          // back into the text re-selects it through the editor's focus event).
          clearTimeout(blurTimerRef.current);
          blurTimerRef.current = setTimeout(() => {
            const toolbar = toolbarRef.current;
            if (toolbar && toolbar.contains(document.activeElement)) return;
            if (!editor.isDestroyed && !editor.view.hasFocus()) setSelected(null);
          }, 0);
        }}
        sx={{
          position: 'absolute',
          top: -6,
          right: 4,
          transform: 'translateY(-100%)',
          display: 'flex',
          alignItems: 'center',
          gap: 0.25,
          height: 24,
          pl: 0.75,
          pr: 0.25,
          borderRadius: 'var(--q-radius-sm, 6px)',
          border: '1px solid var(--q-ai-border, #C9C1F2)',
          bgcolor: 'var(--q-surface, #FFFFFF)',
          pointerEvents: 'none',
          boxShadow: 'var(--q-shadow-sm)',
          '& .MuiIconButton-root': { pointerEvents: 'auto' },
        }}
      >
        <AutoAwesome aria-hidden="true" sx={{ fontSize: 13, color: 'var(--q-ai-text, #3A28B8)' }} />
        <Typography
          component="span"
          data-testid="generated-section-label"
          sx={{ fontSize: 11.5, fontWeight: 600, mx: 0.5, whiteSpace: 'nowrap', color: warn ? 'var(--q-warning-text)' : 'var(--q-ai-text, #3A28B8)' }}
        >
          {info.label}
        </Typography>
        <Tooltip title={regenerateLabel}>
          <IconButton
            size="small"
            aria-label={regenerateLabel}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onRegenerate({ ...info, element: active })}
            sx={{ p: 0.25, color: 'text.secondary' }}
          >
            {info.needsInput ? <AutoAwesome sx={{ fontSize: 16 }} /> : <Refresh sx={{ fontSize: 16 }} />}
          </IconButton>
        </Tooltip>
        {onAsk && (
          <Tooltip title="Ask Inky about this">
            <IconButton
              size="small"
              aria-label="Ask Inky about this"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onAsk({ ...info, element: active })}
              sx={{ p: 0.25, color: 'text.secondary' }}
            >
              <ChatBubbleOutline sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export default memo(GeneratedSectionChrome);
